import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, RefreshControl, StatusBar, Alert, Modal, TextInput,
  ActivityIndicator, Linking, Share, Image, Platform,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY },
});
api.interceptors.request.use(async c => {
  try {
    const t = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (t) c.headers.Authorization = `Bearer ${t}`;
  } catch {}
  return c;
});

// Endpoints — note these use /api/data/* (the real tables) NOT the previous
// /api/applications style routes which never existed. application_interviews
// is the canonical interview table both recruiter and candidate sides read.
const EP = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  applications:   '/api/data/applications',
  interviews:     '/api/data/application_interviews',
  jobs:           '/api/data/jobs',
};

const getBusinessId = async () => {
  try { return await AsyncStorage.getItem('company_id') || '0'; } catch { return '0'; }
};

// Tolerate every response shape — bare arrays, wrapped {data:[]}, etc.
const safeData = r => {
  if (!r) return [];
  const body = r.data ?? r;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.rows)) return body.rows;
  if (Array.isArray(body?.results)) return body.results;
  return [];
};

export default function ContactCandidateScreen({ navigation, route }) {
  // ────────────────────────────────────────────────────────────────────────
  // Two modes:
  //  (1) Focused mode — caller passed a specific applicant in route.params
  //      (from ApplicantDetailsScreen "Message" button). Skip the directory
  //      and show contact actions for that person.
  //  (2) Directory mode — opened from a dashboard/menu with no params. Show
  //      the company's full candidate list so the recruiter can pick.
  // ────────────────────────────────────────────────────────────────────────
  const passedApplicant = route?.params?.applicant || route?.params?.candidate || null;
  const passedApplicantId =
    route?.params?.applicantId ||
    route?.params?.application_id ||
    route?.params?.candidateId ||
    passedApplicant?.id ||
    null;
  const focusedMode = !!(passedApplicant || passedApplicantId);

  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyId, setCompanyId]   = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  // Focused-mode state — the one candidate we're contacting
  const [focused, setFocused] = useState(passedApplicant ? normaliseApplicant(passedApplicant) : null);
  const [focusedInterviews, setFocusedInterviews] = useState([]);

  // Directory-mode state
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({});

  // Compose-message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody]       = useState('');
  const [messageChannel, setMessageChannel] = useState('email'); // email | sms

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setIsLoading(true);
      const b = route?.params?.company_id || await getBusinessId();
      setCompanyId(b);
      if (focusedMode) {
        // Just load company profile + this candidate's interview history
        await Promise.all([loadCompanyProfile(b), loadFocusedApplicant(b)]);
      } else {
        await fetchDirectory(b);
      }
    } catch (e) { console.warn('init:', e?.message); }
    finally    { setIsLoading(false); }
  };

  const onRefresh = async () => { setRefreshing(true); await init(); setRefreshing(false); };

  // ── Company profile ─────────────────────────────────────────────────────
  const loadCompanyProfile = async (bizId) => {
    const cid = parseInt(bizId) || 0;
    if (!cid) return;
    try {
      const r = await api.get(EP.companyProfile(cid));
      const profile = r?.data?.data || r?.data;
      if (profile && typeof profile === 'object') {
        setCompanyProfile(profile);
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile)).catch(() => {});
      }
    } catch (_) {
      try {
        const c = await AsyncStorage.getItem('company_profile');
        if (c) setCompanyProfile(JSON.parse(c));
      } catch {}
    }
  };

  // ── Focused mode load ───────────────────────────────────────────────────
  // Use the passed applicant as the initial render; in the background, fetch
  // the full record from /api/recruitment/applicant/:id so any stale fields
  // get refreshed and we pick up inline interviews/offers for the action bar.
  const loadFocusedApplicant = async (bizId) => {
    if (!passedApplicantId) return;
    try {
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(`${API_BASE_URL}/api/recruitment/applicant/${passedApplicantId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        const row = await res.json();
        if (row && !row.error) {
          setFocused(normaliseApplicant(row));
          if (Array.isArray(row.interviews)) setFocusedInterviews(row.interviews);
        }
      }
    } catch (e) { console.warn('loadFocusedApplicant:', e?.message); }
  };

  // ── Directory mode load ─────────────────────────────────────────────────
  const fetchDirectory = async (bizId) => {
    const cid = parseInt(bizId) || 0;
    if (!cid) { setItems([]); return; }
    await loadCompanyProfile(cid);
    try {
      const params = { company_id: cid, limit: 200 };
      const [appsRes, ivsRes, jobsRes] = await Promise.allSettled([
        api.get(EP.applications, { params }),
        api.get(EP.interviews,   { params }),
        api.get(EP.jobs,         { params }),
      ]);
      const apps      = safeData(appsRes.status === 'fulfilled' ? appsRes.value : null);
      const interviews = safeData(ivsRes.status === 'fulfilled' ? ivsRes.value  : null);
      const jobs      = safeData(jobsRes.status === 'fulfilled' ? jobsRes.value : null);

      const enriched = apps.map(a => ({
        ...a,
        candidateName: a.candidate_name || 'Applicant',
        jobTitle:      a.job_title || jobs.find(j => String(j.id) === String(a.job_id))?.title || '',
        interviewCount: interviews.filter(i => String(i.application_id) === String(a.id)).length,
      }));
      setItems(enriched);
      setStats({
        total:      enriched.length,
        pending:    enriched.filter(a => ['pending', 'new', 'submitted'].includes(a.status)).length,
        interviews: interviews.length,
        hired:      enriched.filter(a => a.status === 'hired').length,
      });
    } catch (e) {
      console.warn('fetchDirectory:', e?.message);
      setItems([]); setStats({});
    }
  };

  // ── Contact actions ─────────────────────────────────────────────────────
  // Open the device's email client. Use the candidate name as a friendly
  // greeting fallback. No fake "<noreply@>" stubs — only the real address.
  // Start a video call with the focused candidate.
  // The candidate-side video screen joins from application_interviews.meeting_link
  // — so for both sides to land in the same Jitsi room, we must persist a row
  // FIRST and only then navigate to VideoScreen with that row's id + link.
  //
  // Flow:
  //   1. Look for an existing scheduled/in_progress interview for this candidate
  //      (the recruiter might be "joining" a call they already scheduled).
  //   2. If found → reuse its id + meeting_link → navigate.
  //   3. If not found → mint a fresh Jitsi room, POST a new application_interviews
  //      row (status=scheduled, type=video), navigate with the new id + link.
  //
  // Either way the recruiter ends up in VideoScreen with the same applicantId,
  // interviewId, and meetingUrl the candidate sees — so the loop closes.
  const startVideoCall = async (candidate) => {
    if (!candidate?.id) {
      Alert.alert('Cannot start', 'No application id for this candidate.');
      return;
    }
    try {
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 1. Look for an existing scheduled/in_progress interview
      let interviewRow = null;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/data/application_interviews?application_id=${candidate.id}&limit=10`,
          { headers }
        );
        if (res.ok) {
          const body = await res.json();
          const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
          // Pick the most recent scheduled or in_progress row that has a link
          interviewRow = rows
            .filter(r => r.meeting_link && ['scheduled', 'in_progress', 'rescheduled'].includes(r.status))
            .sort((a, b) => new Date(b.scheduled_date || 0) - new Date(a.scheduled_date || 0))[0] || null;
        }
      } catch (_) { /* fall through to create */ }

      // 2. If none found, mint a new row
      if (!interviewRow) {
        const roomId = `jobeng-${candidate.id}-${Date.now().toString(36)}`;
        const meetingLink = `https://meet.jit.si/${roomId}`;
        const me = await AsyncStorage.getItem('user_profile').then(s => {
          try { return JSON.parse(s); } catch { return null; }
        });
        const interviewerName = me?.full_name || me?.name || 'Recruiter';
        const interviewerPanel = me?.id ? [{ id: me.id, name: interviewerName, role: me.job_title || 'Recruiter' }] : [];

        const payload = {
          application_id:   parseInt(candidate.id, 10),
          interview_type:   'video',
          scheduled_date:   new Date().toISOString(),
          duration_minutes: 60,
          meeting_link:     meetingLink,
          interviewer_name: interviewerName,
          // JSONB column — pre-stringify or PG rejects as "invalid input syntax"
          interviewers:     interviewerPanel.length ? JSON.stringify(interviewerPanel) : null,
          status:           'scheduled',
          instructions:     'Ad-hoc video call from Contact screen',
        };
        Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);

        const res = await fetch(`${API_BASE_URL}/api/data/application_interviews`, {
          method: 'POST', headers, body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Could not create interview: ${res.status} ${t.slice(0, 200)}`);
        }
        const saved = await res.json().catch(() => ({}));
        interviewRow = { id: saved.id, meeting_link: meetingLink, ...payload };
      }

      // 3. Navigate into VideoScreen with the row context
      navigation.navigate('VideoScreen', {
        applicationId: candidate.id,
        interviewId:   interviewRow.id,
        interviewType: interviewRow.interview_type || 'video',
        meetingUrl:    interviewRow.meeting_link,
        candidate: {
          id:       candidate.id,
          name:     candidate.name,
          email:    candidate.email,
          phone:    candidate.phone,
          jobTitle: candidate.jobTitle,
        },
        interviewers: Array.isArray(interviewRow.interviewers) ? interviewRow.interviewers : [],
        agenda:       interviewRow.instructions || '',
      });
    } catch (e) {
      console.warn('startVideoCall:', e?.message);
      Alert.alert('Could not start call', e?.message || 'Please try again.');
    }
  };

  const openEmail = (candidate) => {
    if (!candidate?.email) {
      Alert.alert('No email on file', `${candidate?.name || 'This candidate'} has no email address.`);
      return;
    }
    const firstName = (candidate.name || '').split(' ')[0] || 'there';
    const subject = encodeURIComponent(
      candidate.jobTitle
        ? `Regarding your application — ${candidate.jobTitle}`
        : 'Regarding your application'
    );
    const body = encodeURIComponent(
      `Hi ${firstName},\n\n` +
      `Thanks for applying${companyProfile?.company_name ? ` to ${companyProfile.company_name}` : ''}.\n\n` +
      `Best,\n`
    );
    Linking.openURL(`mailto:${candidate.email}?subject=${subject}&body=${body}`).catch(() => {
      Alert.alert('Could not open email', 'Please copy the address: ' + candidate.email);
    });
  };

  const openPhone = (candidate) => {
    if (!candidate?.phone) {
      Alert.alert('No phone on file', `${candidate?.name || 'This candidate'} has no phone number.`);
      return;
    }
    Linking.openURL(`tel:${candidate.phone}`).catch(() => {
      Alert.alert('Could not place call', 'Please dial manually: ' + candidate.phone);
    });
  };

  const openSMS = (candidate) => {
    if (!candidate?.phone) {
      Alert.alert('No phone on file', `${candidate?.name || 'This candidate'} has no phone number.`);
      return;
    }
    const firstName = (candidate.name || '').split(' ')[0] || 'there';
    const body = encodeURIComponent(`Hi ${firstName}, this is${companyProfile?.company_name ? ` ${companyProfile.company_name}` : ''} regarding your application.`);
    const sep = Platform.OS === 'ios' ? '&' : '?';
    Linking.openURL(`sms:${candidate.phone}${sep}body=${body}`).catch(() => {
      Alert.alert('Could not open SMS', 'Please send manually to ' + candidate.phone);
    });
  };

  // In-app message — logs to the application's notes/timeline so the team
  // sees a record of what was sent and when. Writes to applicant_notes table.
  const sendInAppMessage = async () => {
    if (!focused?.id) return;
    if (!messageBody.trim()) { Alert.alert('Empty message', 'Write something to send.'); return; }
    try {
      const channel = messageChannel.toUpperCase();
      const subject = messageSubject.trim() || '(no subject)';
      const content = `[${channel}] ${subject}\n\n${messageBody.trim()}`;
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(`${API_BASE_URL}/api/recruitment/applicant/${focused.id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      Alert.alert('Logged', 'Message saved to candidate notes.');
      setShowMessageModal(false);
      setMessageSubject(''); setMessageBody('');
    } catch (e) {
      console.warn('sendInAppMessage:', e?.message);
      Alert.alert('Could not save', e?.message || 'Please try again.');
    }
  };

  // Share — pulls up the OS share sheet so the recruiter can pass details
  // to a colleague via Slack/iMessage/etc.
  const shareCandidate = async (candidate) => {
    try {
      const msg = [
        candidate?.name || 'Candidate',
        candidate?.jobTitle && `Applying for: ${candidate.jobTitle}`,
        candidate?.email   && `Email: ${candidate.email}`,
        candidate?.phone   && `Phone: ${candidate.phone}`,
        candidate?.location && `Location: ${candidate.location}`,
      ].filter(Boolean).join('\n');
      await Share.share({ title: `Candidate — ${candidate?.name || ''}`, message: msg });
    } catch (_) {}
  };

  // ── Renders ─────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SafeAreaView style={st.loading}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color={PRIMARY} />
        <Text style={st.loadingTxt}>Loading...</Text>
      </SafeAreaView>
    );
  }

  // FOCUSED MODE — render the passed candidate's contact card
  if (focusedMode && focused) {
    const initials = (focused.name || 'A').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
    return (
      <SafeAreaView style={st.container}>
        <StatusBar barStyle="light-content" />
        <LinearGradient colors={['#9333EA', '#A855F7']} style={st.header}>
          <View style={st.headerRow}>
            <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
              <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
            </TouchableOpacity>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={st.hTitle}>Contact Candidate</Text>
              <Text style={[st.hSub, { color: '#E9D5FF' }]}>
                {focused.jobTitle ? `Applying for ${focused.jobTitle}` : 'Application contact'}
              </Text>
            </View>
            <TouchableOpacity style={st.hBtn} onPress={() => shareCandidate(focused)}>
              <MaterialCommunityIcons name="share-variant" size={22} color="#FFF" />
            </TouchableOpacity>
          </View>
        </LinearGradient>

        <ScrollView
          style={{ flex: 1 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
          contentContainerStyle={{ paddingBottom: 32 }}
        >
          {/* Candidate identity card */}
          <View style={st.idCard}>
            <View style={[st.bigAvatar, { backgroundColor: PRIMARY + '20' }]}>
              {focused.avatar ? (
                <Image source={{ uri: focused.avatar }} style={st.bigAvatarImg} />
              ) : (
                <Text style={[st.bigAvatarTxt, { color: PRIMARY }]}>{initials}</Text>
              )}
            </View>
            <Text style={st.idName}>{focused.name}</Text>
            {focused.jobTitle ? <Text style={st.idJob}>{focused.jobTitle}</Text> : null}
            {focused.stage ? (
              <View style={st.stageBadge}>
                <Text style={st.stageBadgeTxt}>{focused.stage}</Text>
              </View>
            ) : null}
          </View>

          {/* Quick contact actions */}
          <View style={st.actionsRow}>
            <ContactAction
              icon="email-outline"
              label="Email"
              disabled={!focused.email}
              onPress={() => openEmail(focused)}
            />
            <ContactAction
              icon="phone-outline"
              label="Call"
              disabled={!focused.phone}
              onPress={() => openPhone(focused)}
            />
            <ContactAction
              icon="video-outline"
              label="Video"
              onPress={() => startVideoCall(focused)}
            />
            <ContactAction
              icon="note-edit-outline"
              label="Note"
              onPress={() => {
                setMessageChannel('note');
                setMessageSubject('');
                setMessageBody('');
                setShowMessageModal(true);
              }}
            />
          </View>

          {/* Contact details — real fields from the application row */}
          <View style={st.section}>
            <Text style={st.secTitle}>Contact information</Text>
            <DetailRow icon="email-outline"   label="Email"    value={focused.email}    onPress={() => openEmail(focused)} />
            <DetailRow icon="phone-outline"   label="Phone"    value={focused.phone}    onPress={() => openPhone(focused)} />
            <DetailRow icon="map-marker-outline" label="Location" value={focused.location} />
            <DetailRow icon="briefcase-outline" label="Current"  value={focused.currentTitle ? `${focused.currentTitle}${focused.currentCompany ? ' · ' + focused.currentCompany : ''}` : focused.currentCompany} />
          </View>

          {/* Application context */}
          <View style={st.section}>
            <Text style={st.secTitle}>Application</Text>
            <DetailRow icon="file-document-outline" label="Position" value={focused.jobTitle} />
            <DetailRow icon="progress-check"        label="Stage"    value={focused.stage} />
            <DetailRow icon="calendar-clock"        label="Applied"  value={focused.appliedDate} />
            {focusedInterviews.length > 0 && (
              <DetailRow
                icon="video-outline"
                label="Interviews"
                value={`${focusedInterviews.length} on record`}
              />
            )}
          </View>

          {/* Cross-screen jump-offs */}
          <View style={st.section}>
            <Text style={st.secTitle}>Continue with</Text>
            <JumpButton
              icon="account-details"
              label="View full profile"
              onPress={() => navigation.navigate('ApplicantDetailsScreen', {
                applicantId: focused.id, applicant: focused.raw,
              })}
            />
            <JumpButton
              icon="calendar-plus"
              label="Schedule interview"
              onPress={() => navigation.navigate('ScheduleInterviewScreen', {
                applicantId: focused.id, applicant: focused.raw,
              })}
            />
            <JumpButton
              icon="file-sign"
              label="Make offer"
              onPress={() => navigation.navigate('MakeOfferScreen', {
                applicantId: focused.id, applicant: focused.raw,
              })}
            />
          </View>
        </ScrollView>

        {renderMessageModal()}
      </SafeAreaView>
    );
  }

  // DIRECTORY MODE — old behaviour, list of all candidates
  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" />
      <LinearGradient colors={['#9333EA', '#A855F7']} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.hTitle}>{companyProfile?.company_name || 'Contact Candidates'}</Text>
            <Text style={[st.hSub, { color: '#E9D5FF' }]}>
              {items.length} candidate{items.length === 1 ? '' : 's'} in your pipeline
            </Text>
          </View>
          <View style={st.hBtn} />
        </View>
      </LinearGradient>

      <ScrollView
        style={{ flex: 1 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        contentContainerStyle={{ paddingVertical: 12 }}
        showsVerticalScrollIndicator={false}
      >
        <View style={st.searchRow}>
          <MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
          <TextInput
            style={st.searchInput}
            placeholder="Search by name, email, or job"
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        {items
          .filter(i => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            return (
              (i.candidateName || '').toLowerCase().includes(q) ||
              (i.candidate_email || '').toLowerCase().includes(q) ||
              (i.jobTitle || '').toLowerCase().includes(q)
            );
          })
          .slice(0, 100)
          .map((item) => {
            const initials = (item.candidateName || 'A').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
            return (
              <TouchableOpacity
                key={String(item.id)}
                style={st.card}
                onPress={() => setFocused(normaliseApplicant(item))}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={[st.avatar, { backgroundColor: PRIMARY }]}>
                    <Text style={st.avatarTxt}>{initials}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={st.cardName}>{item.candidateName}</Text>
                    <Text style={st.cardMeta} numberOfLines={1}>
                      {item.jobTitle || item.candidate_email || '—'}
                    </Text>
                  </View>
                  {item.interviewCount > 0 && (
                    <View style={st.badge}>
                      <Text style={st.badgeTxt}>{item.interviewCount} interview{item.interviewCount === 1 ? '' : 's'}</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            );
          })}

        {items.length === 0 && (
          <View style={st.empty}>
            <MaterialCommunityIcons name="account-search" size={48} color="#D1D5DB" />
            <Text style={st.emptyTxt}>No candidates yet</Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );

  // ── Compose-message modal ───────────────────────────────────────────────
  function renderMessageModal() {
    return (
      <Modal
        visible={showMessageModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowMessageModal(false)}
      >
        <SafeAreaView style={{ flex: 1, backgroundColor: '#FFF' }}>
          <View style={st.modalH}>
            <TouchableOpacity style={st.modalClose} onPress={() => setShowMessageModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
            <Text style={st.modalTitle}>Add note for {focused?.name || 'candidate'}</Text>
            <View style={{ width: 40 }} />
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={st.formLbl}>Subject (optional)</Text>
            <TextInput
              style={st.formInput}
              placeholder="e.g. Follow-up sent via email"
              placeholderTextColor="#9CA3AF"
              value={messageSubject}
              onChangeText={setMessageSubject}
            />
            <View style={{ height: 16 }} />
            <Text style={st.formLbl}>Note</Text>
            <TextInput
              style={[st.formInput, { minHeight: 160, textAlignVertical: 'top' }]}
              multiline
              placeholder="What did you communicate to this candidate?"
              placeholderTextColor="#9CA3AF"
              value={messageBody}
              onChangeText={setMessageBody}
            />
            <TouchableOpacity style={st.submitBtn} onPress={sendInAppMessage}>
              <MaterialCommunityIcons name="content-save-outline" size={20} color="#FFF" />
              <Text style={st.submitBtnTxt}>Save to candidate notes</Text>
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  }
}

// Tiny presentational components — kept in this file because they're not
// reused elsewhere and inlining them inflates the JSX above considerably.
function ContactAction({ icon, label, onPress, disabled }) {
  return (
    <TouchableOpacity
      style={[st.action, disabled && { opacity: 0.4 }]}
      onPress={onPress}
      disabled={disabled}
    >
      <View style={st.actionIcon}>
        <MaterialCommunityIcons name={icon} size={22} color={PRIMARY} />
      </View>
      <Text style={st.actionLbl}>{label}</Text>
    </TouchableOpacity>
  );
}

function DetailRow({ icon, label, value, onPress }) {
  if (!value) return null;
  const inner = (
    <View style={st.detailRow}>
      <MaterialCommunityIcons name={icon} size={18} color="#6B7280" />
      <View style={{ flex: 1, marginLeft: 12 }}>
        <Text style={st.detailLbl}>{label}</Text>
        <Text style={st.detailVal} numberOfLines={2}>{value}</Text>
      </View>
      {onPress ? <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" /> : null}
    </View>
  );
  return onPress ? <TouchableOpacity onPress={onPress}>{inner}</TouchableOpacity> : inner;
}

function JumpButton({ icon, label, onPress }) {
  return (
    <TouchableOpacity style={st.jumpBtn} onPress={onPress}>
      <MaterialCommunityIcons name={icon} size={20} color={PRIMARY} />
      <Text style={st.jumpLbl}>{label}</Text>
      <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
    </TouchableOpacity>
  );
}

// Map any incoming applicant shape (passed via route, fetched from API, or
// pulled from the directory list) into the single camelCase form the focused
// view reads. Avoids the "Applicant" / "Unknown" placeholders by falling
// through both candidate_name and applicant_name.
function normaliseApplicant(raw) {
  if (!raw) return null;
  return {
    id:       raw.id || raw.application_id,
    name:     raw.candidate_name || raw.applicant_name || raw.name || 'Applicant',
    email:    raw.candidate_email || raw.applicant_email || raw.email || '',
    phone:    raw.candidate_phone || raw.applicant_phone || raw.phone || '',
    avatar:   raw.candidate_avatar || raw.avatar || raw.avatar_url || null,
    location: raw.candidate_location || raw.location || '',
    jobId:    raw.job_id || raw.jobId,
    jobTitle: raw.job_title || raw.jobTitle || raw.position || '',
    stage:    raw.stage || raw.application_stage || '',
    currentTitle:   raw.current_title || raw.currentTitle || '',
    currentCompany: raw.current_company || raw.currentCompany || '',
    appliedDate: raw.created_at || raw.applied_date || raw.appliedDate || '',
    raw,
  };
}

const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F3F4F6' },
  loading:   { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F3F4F6' },
  loadingTxt:{ fontSize: 16, color: '#6B7280', marginTop: 16 },

  header:    { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn:      { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle:    { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  hSub:      { fontSize: 13, marginTop: 2 },

  // Identity card (focused mode)
  idCard:    { alignItems: 'center', paddingVertical: 24, paddingHorizontal: 16, backgroundColor: '#FFF' },
  bigAvatar: { width: 88, height: 88, borderRadius: 44, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  bigAvatarImg: { width: 88, height: 88, borderRadius: 44 },
  bigAvatarTxt: { fontSize: 28, fontWeight: '700' },
  idName:    { fontSize: 22, fontWeight: '700', color: '#0F172A' },
  idJob:     { fontSize: 14, color: '#475569', marginTop: 4 },
  stageBadge:{ marginTop: 10, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, backgroundColor: '#EEF2FF' },
  stageBadgeTxt: { fontSize: 11, fontWeight: '600', color: '#4338CA', textTransform: 'capitalize' },

  // Action row
  actionsRow: { flexDirection: 'row', justifyContent: 'space-around', paddingVertical: 16, backgroundColor: '#FFF', borderTopWidth: 1, borderTopColor: '#F3F4F6' },
  action:    { alignItems: 'center', flex: 1 },
  actionIcon:{ width: 44, height: 44, borderRadius: 22, backgroundColor: PRIMARY + '15', alignItems: 'center', justifyContent: 'center', marginBottom: 4 },
  actionLbl: { fontSize: 12, color: '#334155', fontWeight: '500' },

  // Generic section card
  section:   { backgroundColor: '#FFF', marginTop: 12, marginHorizontal: 16, padding: 16, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  secTitle:  { fontSize: 15, fontWeight: '700', color: '#0F172A', marginBottom: 12 },

  // Detail rows
  detailRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  detailLbl: { fontSize: 11, color: '#94A3B8', fontWeight: '500', textTransform: 'uppercase', letterSpacing: 0.5 },
  detailVal: { fontSize: 14, color: '#0F172A', marginTop: 2 },

  // Jump buttons
  jumpBtn:   { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 4, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  jumpLbl:   { flex: 1, fontSize: 14, color: '#0F172A', marginLeft: 12, fontWeight: '500' },

  // Directory mode bits
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, marginHorizontal: 16, marginBottom: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  searchInput: { flex: 1, fontSize: 14, color: '#1F2937', marginLeft: 8 },
  card:      { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 8, padding: 14, borderRadius: 10, borderWidth: 1, borderColor: '#E5E7EB' },
  cardName:  { fontSize: 15, fontWeight: '600', color: '#1F2937' },
  cardMeta:  { fontSize: 12, color: '#6B7280', marginTop: 2 },
  avatar:    { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  avatarTxt: { color: '#FFF', fontWeight: 'bold', fontSize: 14 },
  badge:     { borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3, backgroundColor: '#EEF2FF' },
  badgeTxt:  { fontSize: 11, fontWeight: '600', color: '#4338CA' },
  empty:     { alignItems: 'center', padding: 48 },
  emptyTxt:  { fontSize: 16, color: '#6B7280', marginTop: 12 },

  // Modal
  modalH:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle:{ fontSize: 17, fontWeight: '700', color: '#1F2937', flex: 1, textAlign: 'center' },
  modalClose:{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' },
  formLbl:   { fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  formInput: { backgroundColor: '#F3F4F6', borderRadius: 8, padding: 12, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB' },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: PRIMARY, borderRadius: 10, padding: 14, marginTop: 20 },
  submitBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 15, marginLeft: 8 },
});
